<template>
    <div class="placement-wizard" :class="{ 'tablet-mode': isTablet }">
        <!-- Hidden Scanner Input Field for Mobile/Tablet Zebra Scanners (TC200 & TC22) -->
        <input
            v-if="!isDesktop"
            ref="scannerInput"
            type="text"
            inputmode="none"
            :class="scannerOptions.debug ? 'scanner-input-visible' : 'scanner-input-hidden'"
            @input="handleScannerInput"
            @keydown="handleScannerKeydown"
            @focus="handleScannerFocus"
            @blur="handleScannerBlur"
            autocomplete="off"
        />

        <!-- Tablet Mode Badge (Tablet Only, not Mobile) -->
        <div v-if="isTablet && !isMobile" class="tablet-mode-badge">
            <v-chip color="primary" dark small>
                <v-icon left small>mdi-warehouse</v-icon>
                Einlagern
            </v-chip>
            <div class="socket-debug-info">
                <v-chip x-small color="warning" dark>
                    {{ state }}
                </v-chip>
            </div>
        </div>

        <!-- Dev Mode Toggle -->
        <div v-if="isDesktop" class="dev-mode-toggle">
            <v-btn
                color="orange"
                dark
                small
                fab
                @click="devModeEnabled = !devModeEnabled"
                :class="{ 'dev-active': devModeEnabled }"
            >
                <v-icon small>mdi-bug</v-icon>
            </v-btn>
        </div>

        <!-- Back Button for Small Devices -->
        <div v-if="isMobile" class="back-button">
            <v-btn
                color="grey darken-2"
                dark
                small
                fab
                @click="goBack"
            >
                <v-icon small>mdi-arrow-left</v-icon>
            </v-btn>
        </div>

        <!-- Fullscreen Toggle for Tablet and Mobile -->
        <div v-if="!isDesktop && fullscreenSupported" class="fullscreen-toggle">
            <v-btn
                color="blue"
                dark
                small
                fab
                @click="isFullscreen ? exitFullscreen() : enterFullscreen()"
            >
                <v-icon small>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
        </div>

        <!-- Main Content Area - Flexbox Layout -->
        <div class="placement-main-container">
            <!-- Left Side: Placement Wizard States -->
            <div class="placement-content-wrapper">
                <!-- Loading Overlay -->
                <div v-if="isProcessing" class="loading-overlay">
                    <div class="loading-content">
                        <v-progress-circular
                            :size="loadingSize"
                            :width="loadingWidth"
                            color="primary"
                            indeterminate
                        ></v-progress-circular>
                        <div class="loading-text" :class="loadingTextClass">{{ loadingMessage }}</div>
                    </div>
                </div>

                <!-- Use new state components -->
                <idle-state v-if="state === 'idle'" />

                <scan-start-state
                    v-else-if="state === 'scanstart'"
                    :article-data="mockData"
                />

                <error-slot-state
                    v-else-if="state === 'error_slot'"
                    :scanned-location="mockData.scannedLocation"
                    :expected-location="mockData.shelfLocation"
                    :requires-extra-slot="mockData.requiresExtraSlot"
                    :original-slot="mockData.originalSlot"
                    :countdown="countdown"
                    @retry="resetToScanStart"
                />

                <error-article-state
                    v-else-if="state === 'error_article'"
                    :article-sku="scannedArticle ? scannedArticle.sku : mockData.wrongSku"
                    :article-location="mockData.wrongArticleLocation"
                    :original-slot="mockData.wrongArticleOriginalSlot"
                    :requires-extra-slot="mockData.requiresExtraSlot"
                    :error-reason="mockData.errorReason"
                    :countdown="countdown"
                    :print-preview="mockData.printPreview"
                    @reset="reset"
                />

                <success-state
                    v-else-if="state === 'success'"
                    :shelf-location="mockData.shelfLocation"
                    :order-id="mockData.orderId"
                    :sku="mockData.sku"
                    :requires-extra-slot="mockData.requiresExtraSlot"
                    :original-slot="mockData.originalSlot"
                    :countdown="countdown"
                />
            </div>

            <!-- Right Side: Unconfirmed Articles Table (Desktop Only) -->
            <div v-if="isDesktop" class="unconfirmed-section">
                <v-card class="unconfirmed-card" elevation="2">
                    <v-card-title class="unconfirmed-header">
                        <div class="d-flex align-items-center justify-space-between" style="width: 100%;">
                            <div class="d-flex align-items-center">
                                <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
                                <span class="text-h6">Unbestätigte Artikel</span>
                                <v-chip small color="primary" class="ml-3">
                                    {{ filteredUnconfirmedArticles.length }} / {{ unconfirmedArticles.length }}
                                </v-chip>
                            </div>
                            <v-btn
                                color="secondary"
                                @click="fetchUnconfirmedArticles"
                                small
                                :loading="loadingUnconfirmed"
                                icon
                            >
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                        </div>
                    </v-card-title>

                    <v-card-text class="pa-0 unconfirmed-card-content">
                        <!-- Search Field -->
                        <div class="search-container pa-4 pb-2">
                            <v-text-field
                                v-model="searchQuery"
                                placeholder="Suche nach Bestellung, SKU oder Personalisierung..."
                                prepend-inner-icon="mdi-magnify"
                                clearable
                                outlined
                                dense
                                hide-details
                            ></v-text-field>
                        </div>

                        <!-- Table Container -->
                        <div class="table-scroll-container">
                            <!-- Loading State -->
                            <div v-if="loadingUnconfirmed" class="text-center pa-8">
                                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                <p class="mt-4 text--secondary">Lade unbestätigte Artikel...</p>
                            </div>

                            <!-- Data Table -->
                            <v-data-table
                                v-else-if="unconfirmedArticles.length > 0"
                                :headers="[
                                    { text: 'Regal', value: 'slot', align: 'start', sortable: true },
                                    { text: 'Bestellung', value: 'orderId', align: 'start', sortable: true },
                                    { text: 'SKU', value: 'sku', align: 'start', sortable: true },
                                    { text: 'Menge', value: 'qty', align: 'center', sortable: true },
                                    { text: 'Personalisierung', value: 'line1', align: 'start', sortable: false },
                                    { text: 'Hashcode', value: 'hashcode', align: 'start', sortable: true }
                                ]"
                                :items="filteredUnconfirmedArticles"
                                :items-per-page="-1"
                                :item-class="getRowClass"
                                class="unconfirmed-table"
                                dense
                                hide-default-footer
                            >
                                <template v-slot:[`item.slot`]="{ item }">
                                    <v-chip small color="primary" dark>
                                        <v-icon left x-small>mdi-warehouse</v-icon>
                                        {{ item.slot }}
                                    </v-chip>
                                </template>

                                <template v-slot:[`item.orderId`]="{ item }">
                                    <div class="table-cell-content">
                                        <span class="font-weight-medium text-truncate" style="max-width: 120px; display: inline-block;">{{ item.orderId }}</span>
                                    </div>
                                </template>

                                <template v-slot:[`item.sku`]="{ item }">
                                    <div class="table-cell-content">
                                        <code class="text-caption text-truncate" style="max-width: 150px; display: inline-block;">{{ item.sku }}</code>
                                    </div>
                                </template>

                                <template v-slot:[`item.qty`]="{ item }">
                                    <div class="table-cell-content">
                                        <v-chip x-small :color="item.qty > 1 ? 'orange' : 'grey'" dark>
                                            {{ item.qty }}x
                                        </v-chip>
                                    </div>
                                </template>

                                <template v-slot:[`item.line1`]="{ item }">
                                    <div class="table-cell-content">
                                        <span v-if="item.line1" class="text-truncate" style="max-width: 200px; display: inline-block;">
                                            {{ item.line1 }}
                                        </span>
                                        <span v-else class="text--disabled">—</span>
                                    </div>
                                </template>

                                <template v-slot:[`item.hashcode`]="{ item }">
                                    <div class="table-cell-content">
                                        <code class="text-caption text--secondary text-truncate" style="max-width: 150px; display: inline-block;">{{ item.hashcode }}</code>
                                    </div>
                                </template>

                                <template v-slot:no-results>
                                    <div class="text-center pa-8">
                                        <v-icon size="64" color="orange" class="mb-4">mdi-magnify-close</v-icon>
                                        <p class="text-h6">Keine Ergebnisse</p>
                                        <p class="text--secondary">Keine Artikel gefunden für "{{ searchQuery }}"</p>
                                    </div>
                                </template>
                            </v-data-table>

                            <!-- Empty State -->
                            <div v-else class="text-center pa-8">
                                <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
                                <p class="text-h6">Keine unbestätigten Artikel</p>
                                <p class="text--secondary">Alle Artikel wurden erfolgreich eingelagert.</p>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <!-- Dev Mode Panel -->
        <dev-mode-panel
            v-if="isDesktop"
            :enabled="devModeEnabled"
            :current-state="state"
            @close="devModeEnabled = false"
            @set-state="handleDevSetState"
            @simulate-article-scan="handleDevArticleScan"
            @simulate-shelf-scan="handleDevShelfScan"
        />

        <!-- Snackbar for notifications (Desktop Only) -->
        <v-snackbar
            v-if="isDesktop"
            v-model="snackbar.show"
            :color="snackbar.color"
            :timeout="snackbar.timeout"
            top
            right
        >
            {{ snackbar.message }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    text
                    v-bind="attrs"
                    @click="snackbar.show = false"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import PacklistService from '@/services/PacklistService';
import scannerInputMixin from '@/mixins/scannerInputMixin';
import '@/styles/scanner-input.css';
import DevModePanel from './components/DevModePanel.vue';
import IdleState from './components/IdleState.vue';
import ScanStartState from './components/ScanStartState.vue';
import ErrorSlotState from './components/ErrorSlotState.vue';
import ErrorArticleState from './components/ErrorArticleState.vue';
import SuccessState from './components/SuccessState.vue';

export default {
    name: 'MulticartPlacement',
    mixins: [scannerInputMixin],
    components: {
        DevModePanel,
        IdleState,
        ScanStartState,
        ErrorSlotState,
        ErrorArticleState,
        SuccessState
    },
    data() {
        return {
            state: 'idle', // idle, scanstart, error_slot, error_article, success
            currentTime: '',
            countdown: 5,
            countdownInterval: null,

            // Dev mode
            devModeEnabled: false,

            // Scanner options (from mixin)
            scannerOptions: {
                enabled: true,
                refocusDelay: 3000,
                debug: false
            },

            // Scanned article data
            scannedArticle: null, // Will store: { orderId, sku, slot, hashcode }
            scannedShelfSlot: null, // The slot that was scanned for verification

            // Mock data
            mockData: {
                orderId: 'AMZ-123456789',
                sku: 'KEYCHAIN-PHOTO-001',
                productName: 'Schlüsselanhänger mit Foto',
                quantity: 2,
                printPreview: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE4IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj5QcmludCBQcmV2aWV3PC90ZXh0Pjwvc3ZnPg==',
                shelfLocation: 'W-A-15',
                originalSlot: '',
                requiresExtraSlot: false,
                scannedLocation: 'W-B-08',
                wrongSku: 'KEYCHAIN-WOOD-002',
                wrongArticleLocation: 'W-C-12',
                wrongArticleOriginalSlot: '',
                errorReason: '' // 'not_found' or 'already_scanned'
            },

            // Tablet mode
            windowWidth: window.innerWidth,
            isFullscreen: false,
            fullscreenSupported: false,

            // Snackbar
            snackbar: {
                show: false,
                message: '',
                color: 'success',
                timeout: 3000
            },

            // Unconfirmed Articles
            unconfirmedArticles: [],
            loadingUnconfirmed: false,
            searchQuery: '',
            foundArticleHashcode: null,

            // Loading state
            isProcessing: false,
            loadingMessage: ''
        };
    },
    computed: {
        isSmallScreen() {
            return this.windowWidth <= 480;
        },
        loadingSize() {
            // Larger spinner for mobile devices
            if (this.isMobile) return 80;
            if (this.isTablet) return 70;
            return 60;
        },
        loadingWidth() {
            // Thicker spinner for mobile devices
            if (this.isMobile) return 8;
            if (this.isTablet) return 7;
            return 6;
        },
        loadingTextClass() {
            if (this.isMobile) return 'text-h6';
            if (this.isTablet) return 'text-h6';
            return 'text-body-1';
        },
        isTablet() {
            // Check for query parameter override first
            if (this.$route.query.tablet === 'true' || this.$route.query.tablet === '1') {
                return true;
            }

            // Enhanced tablet detection with better portrait support
            const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const width = this.windowWidth;
            const height = window.innerHeight;

            // Must have touch capability to be considered tablet
            if (!hasTouch) return false;

            // Handle both landscape and portrait orientations for tablets
            const isTabletLandscape = width >= 768 && width <= 1366 && width > height;
            const isTabletPortrait = height >= 768 && height <= 1366 && height > width && width >= 600;
            const isLargeTablet = (width >= 768 || height >= 768) && Math.max(width, height) <= 1366;

            // Too small to be a tablet (phone size)
            if (Math.max(width, height) < 768) return false;

            return isTabletLandscape || isTabletPortrait || isLargeTablet;
        },

        isMobile() {
            // Mobile: small screen devices (phones)
            return this.windowWidth < 768;
        },

        isDesktop() {
            // Desktop: not tablet and not mobile
            return !this.isTablet && !this.isMobile;
        },

        filteredUnconfirmedArticles() {
            if (!this.searchQuery || this.searchQuery.trim() === '') {
                return this.unconfirmedArticles;
            }

            const query = this.searchQuery.toLowerCase().trim();

            return this.unconfirmedArticles.filter(article => {
                // Search in orderId
                const matchesOrderId = article.orderId && article.orderId.toString().toLowerCase().includes(query);

                // Search in SKU
                const matchesSku = article.sku && article.sku.toLowerCase().includes(query);

                // Search in personalization (line1)
                const matchesLine1 = article.line1 && article.line1.toLowerCase().includes(query);

                return matchesOrderId || matchesSku || matchesLine1;
            });
        }
    },
    watch: {
        state(newVal, oldVal) {
            // Clear any existing countdown when state changes
            this.stopCountdown();

            if (newVal === 'success') {
                this.updateTime();
                this.countdown = 5;
                this.startCountdown();
            } else if (newVal === 'error_article' || newVal === 'error_slot') {
                // Set countdown to 10 seconds for error states
                this.countdown = 10;
                this.startCountdown();
            }
        },

        // Watch for tablet mode changes to handle fullscreen
        isTablet: {
            handler(newVal, oldVal) {
                if (newVal && !oldVal) {
                    // Entered tablet mode - request fullscreen
                    this.$nextTick(() => {
                        this.handleTabletFullscreen();
                    });
                } else if (!newVal && oldVal && this.isFullscreen) {
                    // Exited tablet mode - exit fullscreen
                    this.exitFullscreen();
                }
            }
        }
    },
    methods: {
        goBack() {
            this.$router.push('/warehouse/task-choice');
        },

        reset() {
            this.stopCountdown();
            this.state = 'idle';
            this.countdown = 5;

            // Clear scanned data
            this.scannedArticle = null;
            this.scannedShelfSlot = null;

            // Clear found article highlight
            this.foundArticleHashcode = null;

            // Clear error display data
            this.mockData.scannedLocation = '';
            this.mockData.wrongSku = '';
            this.mockData.wrongArticleLocation = '';
            this.mockData.notFoundReason = '';
            this.mockData.scannedArticleDetails = null;

            this.showSnackbar('🔄 Reset complete', 'info');
        },

        resetToScanStart() {
            this.stopCountdown();
            this.state = 'scanstart';
            this.countdown = 5;
        },

        startCountdown() {
            // Don't reset countdown here - it's set by the state watcher
            this.countdownInterval = setInterval(() => {
                this.countdown--;
                if (this.countdown <= 0) {
                    // If in error_slot state, go back to scanstart instead of idle
                    if (this.state === 'error_slot') {
                        this.resetToScanStart();
                    } else {
                        this.reset();
                    }
                }
            }, 1000);
        },

        stopCountdown() {
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval);
                this.countdownInterval = null;
            }
        },

        updateTime() {
            const now = new Date();
            this.currentTime = now.toLocaleTimeString('de-DE');
        },

        // Snackbar helper
        showSnackbar(message, color = 'success', timeout = 3000) {
            this.snackbar = {
                show: true,
                message,
                color,
                timeout
            };
        },

        // Handle window resize for tablet detection
        handleResize() {
            this.windowWidth = window.innerWidth;
        },

        // Fullscreen management
        checkFullscreenSupport() {
            this.fullscreenSupported = !!(
                document.fullscreenEnabled ||
                document.webkitFullscreenEnabled ||
                document.mozFullScreenEnabled ||
                document.msFullscreenEnabled
            );
        },

        async enterFullscreen() {
            if (!this.fullscreenSupported) {
                console.warn('[Tablet] Fullscreen not supported');
                return;
            }

            try {
                const element = document.documentElement;

                if (element.requestFullscreen) {
                    await element.requestFullscreen();
                } else if (element.webkitRequestFullscreen) {
                    await element.webkitRequestFullscreen();
                } else if (element.mozRequestFullScreen) {
                    await element.mozRequestFullScreen();
                } else if (element.msRequestFullscreen) {
                    await element.msRequestFullscreen();
                }

                console.log('[Tablet] Entered fullscreen mode');
            } catch (error) {
                console.error('[Tablet] Error entering fullscreen:', error);
            }
        },

        async exitFullscreen() {
            try {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    await document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    await document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    await document.msExitFullscreen();
                }

                console.log('[Tablet] Exited fullscreen mode');
            } catch (error) {
                console.error('[Tablet] Error exiting fullscreen:', error);
            }
        },

        handleFullscreenChange() {
            const isFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );

            this.isFullscreen = isFullscreen;
            console.log('[Device] Fullscreen state changed:', isFullscreen);

            // If we exit fullscreen and we're in tablet/mobile mode, try to re-enter
            if (!isFullscreen && (this.isTablet || this.isMobile) && (this.$route.query.tablet || this.$route.query.mobile)) {
                setTimeout(() => {
                    this.enterFullscreen();
                }, 1000);
            }
        },

        async handleTabletFullscreen() {
            if ((this.isTablet || this.isMobile) && (this.$route.query.tablet || this.$route.query.mobile) && this.fullscreenSupported && !this.isFullscreen) {
                console.log('[Device] Auto-entering fullscreen for mobile/tablet mode');
                await this.enterFullscreen();
            }
        },

        setupTabletViewport() {
            let viewportMeta = document.querySelector('meta[name="viewport"]');

            if (this.isTablet || this.isMobile) {
                const mobileViewport = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';

                if (viewportMeta) {
                    viewportMeta.setAttribute('content', mobileViewport);
                } else {
                    viewportMeta = document.createElement('meta');
                    viewportMeta.name = 'viewport';
                    viewportMeta.content = mobileViewport;
                    document.head.appendChild(viewportMeta);
                }
                console.log('[Device] Viewport optimized for mobile/tablet');
            }
        },

        // Scanner Methods (via scannerInputMixin)
        /**
         * Handle scan from DataWedge (called by scannerInputMixin)
         * This method is called when a complete scan is received from TC200 or TC22
         */
        onScanReceived(scannedValue) {
            console.log('🔍 [MulticartPlacement] Processing scan from DataWedge:', scannedValue);

            // Parse and handle the scanned data
            const parsedData = this.parseScanData(scannedValue);

            if (parsedData) {
                console.log('✅ Parsed scan data:', parsedData);
                this.handleScannedData(parsedData);
            } else {
                console.error('❌ Invalid barcode format:', scannedValue);
                this.showSnackbar('❌ Ungültiges Barcode-Format', 'error');
            }
        },

        parseScanData(scanString) {
            // Try to parse as shelf location JSON first: { L: "U-W-E7" } or { L: "U-A-A5" } or { L: "SPERRGUT_MESSLATTE" }
            try {
                const jsonData = JSON.parse(scanString);
                if (jsonData.L) {
                    const originalSlot = jsonData.L;

                    // Check if this is an extra slot (starts with SPERRGUT_ or other extra slot patterns)
                    if (originalSlot.startsWith('SPERRGUT_')) {
                        console.log('📍 Extra slot scan detected:', originalSlot);
                        return {
                            type: 'shelf',
                            slot: originalSlot // Keep extra slot as-is
                        };
                    }

                    // Remove prefix pattern like U-A-, U-W-, U-B-, etc. for normal slots
                    // Matches pattern: U-[any letters]-[actual slot]
                    const cleanedSlot = originalSlot.replace(/^U-[A-Z]+-/i, '');

                    console.log('📍 Shelf scan parsing:', {
                        original: originalSlot,
                        cleaned: cleanedSlot,
                        removed: originalSlot.replace(cleanedSlot, '')
                    });

                    return {
                        type: 'shelf',
                        slot: cleanedSlot
                    };
                }
            } catch (e) {
                // Not JSON, continue with article format parsing
            }

            // Parse article format for US keyboard: MM12345|box_teein|A1|1234567890
            // Parts: orderId, sku, location (slot), hashcode
            const parts = scanString.split('|');

            if (parts.length >= 4) {
                return {
                    type: 'article',
                    orderId: parts[0],
                    sku: parts[1],
                    slot: parts[2], // Location/slot where article should be placed
                    hashcode: parts[3]
                };
            }

            // Fallback: German keyboard format MM12345'box?teein'A1'1234567890
            const altParts = scanString.split("'");
            if (altParts.length >= 4) {
                return {
                    type: 'article',
                    orderId: altParts[0],
                    sku: altParts[1].replace(/\?/g, '_'), // Replace ? with _ for SKU
                    slot: altParts[2],
                    hashcode: altParts[3]
                };
            }

            return null;
        },

        async handleScannedData(scannedData) {
            console.log('📦 Handling scanned data:', scannedData);

            // Check scan type
            if (scannedData.type === 'article') {
                // Article QR code scanned

                // If we're in any state other than idle, reset and start fresh with new article
                if (this.state !== 'idle') {
                    console.log('🔄 New article scanned during active state, resetting workflow...');
                    this.showSnackbar('🔄 Neuer Artikel erkannt, Workflow wird neu gestartet', 'info');
                    this.reset();
                    // Give a small delay to allow reset to complete
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

                await this.handleArticleScan(scannedData);
            } else if (scannedData.type === 'shelf') {
                // Shelf QR code scanned
                if (this.state === 'scanstart') {
                    await this.handleShelfScan(scannedData);
                } else if (this.state === 'idle') {
                    this.showSnackbar('⚠️ Bitte scannen Sie zuerst einen Artikel', 'warning');
                } else if (this.state === 'error_slot') {
                    // Allow retrying shelf scan from error_slot state
                    await this.handleShelfScan(scannedData);
                } else {
                    this.showSnackbar('⚠️ Bitte scannen Sie zuerst einen Artikel', 'warning');
                }
            }
        },

        async handleArticleScan(scannedData) {
            console.log('📦 Article scanned:', scannedData);

            // Show loading indicator
            this.isProcessing = true;
            this.loadingMessage = 'Artikel wird geprüft...';

            try {
                // Check if article exists in unconfirmed list (backend check)
                const result = await this.checkArticleInShelf(scannedData.hashcode);

                if (result.status === 'ARTICLE_NOT_FOUND') {
                    // Article doesn't exist in the unconfirmed list at all
                    console.log('❌ Article not found in unconfirmed list:', scannedData.hashcode);
                    this.mockData.wrongSku = scannedData.sku;
                    this.mockData.wrongArticleLocation = '';
                    this.mockData.errorReason = 'not_found';
                    this.showSnackbar('❌ Artikel nicht in der Liste gefunden', 'error');
                    this.state = 'error_article';
                    return;
                }

                if (result.status === 'ARTICLE_ALREADY_SCANNED') {
                    // Article exists but is already confirmed
                    console.log('⚠️ Article already scanned:', scannedData.hashcode);
                    this.mockData.wrongSku = result.article.sku;
                    // Use displaySlot if available (extra slot), otherwise use original slot
                    this.mockData.wrongArticleLocation = result.article.displaySlot || result.article.slot;
                    this.mockData.wrongArticleOriginalSlot = result.article.slot;
                    this.mockData.requiresExtraSlot = result.article.requiresExtraSlot || false;
                    this.mockData.errorReason = 'already_scanned';

                    // Set print preview from article data
                    const line1 = result.article.translated?.line1 || result.article.line1 || 'Keine Vorschau verfügbar';
                    this.mockData.printPreview = line1;

                    this.showSnackbar('⚠️ Artikel bereits gescannt', 'warning');
                    this.state = 'error_article';
                    return;
                }

                // Article is ARTICLE_FOUND and not confirmed yet, proceed with placement
                console.log('✅ Article found in unconfirmed list, proceeding with placement');

                // Use article data from backend response
                const article = result.article;

                // Use displaySlot (which may be extra slot) instead of original slot
                const targetSlot = article.displaySlot || article.slot;
                const isExtraSlot = article.requiresExtraSlot || false;

                console.log('📍 Slot information:', {
                    originalSlot: article.slot,
                    displaySlot: article.displaySlot,
                    targetSlot: targetSlot,
                    requiresExtraSlot: isExtraSlot,
                    extraSlotInfo: article.extraSlotInfo
                });

                // Store the scanned article data with original slot for validation
                this.scannedArticle = {
                    orderId: article.orderId,
                    sku: article.sku,
                    slot: article.slot, // Keep original slot for backend validation
                    displaySlot: targetSlot, // Store display slot for reference
                    hashcode: article.hashcode,
                    requiresExtraSlot: isExtraSlot
                };

                // Update mock data for display - show the slot user should scan
                this.mockData.orderId = article.orderId;
                this.mockData.sku = article.sku;
                this.mockData.shelfLocation = targetSlot; // Show target slot to user
                this.mockData.originalSlot = article.slot; // Original assigned slot
                this.mockData.requiresExtraSlot = isExtraSlot; // Flag for extra slot

                // Get line1 from article.translated.line1 or article.line1
                const line1 = article.translated?.line1 || article.line1 || 'Keine Vorschau verfügbar';
                this.mockData.printPreview = line1;

                // Show appropriate message based on slot type
                if (isExtraSlot) {
                    this.showSnackbar(`✅ Artikel gescannt: ${article.sku}\n📍 Sperrgut-Regal: ${targetSlot}`, 'info', 4000);
                } else {
                    this.showSnackbar(`✅ Artikel gescannt: ${article.sku}`, 'success');
                }

                // Transition to scanstart state
                this.state = 'scanstart';
            } catch (error) {
                console.error('❌ Error checking article status:', error);
                this.showSnackbar('❌ Fehler beim Prüfen des Artikelstatus', 'error');
                // Show error and stop
                this.state = 'idle';
            } finally {
                // Hide loading indicator
                this.isProcessing = false;
                this.loadingMessage = '';
            }
        },

        async checkArticleInShelf(hashcode) {
            // Always call real backend API (dev mode just helps with data input)
            try {
                console.log('🔍 Checking article status for hashcode:', hashcode);
                const response = await PacklistService.getMulticartArticleStatus(hashcode);

                if (response.status === 200 && response.data) {
                    console.log('📋 Article found (200):', response.data);
                    return {
                        status: 'ARTICLE_FOUND',
                        article: response.data.article
                    };
                }

                // If we get here, something unexpected happened
                console.warn('⚠️ Unclear API response:', response);
                throw new Error('Unclear API response');
            } catch (error) {
                // Check if it's a 404 response (article not found in list)
                if (error.response && error.response.status === 404) {
                    console.log('📋 Article not found (404):', error.response.data);
                    return {
                        status: 'ARTICLE_NOT_FOUND',
                        article: null
                    };
                }

                // Check if it's a 400 response (article already scanned)
                if (error.response && error.response.status === 400) {
                    console.log('⚠️ Article already scanned (400):', error.response.data);
                    return {
                        status: 'ARTICLE_ALREADY_SCANNED',
                        article: error.response.data.article
                    };
                }

                // For other errors, log and re-throw
                console.error('❌ Error calling article status API:', error);
                throw error;
            }
        },

        async handleShelfScan(scannedData) {
            console.log('📍 Shelf scanned:', scannedData);

            // Extract shelf location from scanned data
            const scannedShelf = scannedData.slot;

            console.log('📍 Scanned shelf:', scannedShelf);
            console.log('📍 Article original slot:', this.scannedArticle.slot);
            console.log('📍 Article display slot:', this.scannedArticle.displaySlot);
            console.log('📍 Requires extra slot:', this.scannedArticle.requiresExtraSlot);

            // Store the scanned shelf location
            this.scannedShelfSlot = scannedShelf;

            // Show loading indicator
            this.isProcessing = true;
            this.loadingMessage = 'Artikel wird eingelagert...';

            // ALWAYS call backend API - let server validate everything
            try {
                console.log('📡 Calling backend API to validate and confirm placement...');

                // Send the scanned shelf to backend for validation
                const payload = {
                    ...this.scannedArticle,
                    slot: scannedShelf // Use the scanned shelf, not the expected one
                };

                const response = await PacklistService.placeMulticartItem(payload);

                if (response.status === 200 && response.data.success) {
                    // Successfully placed
                    console.log('✅ Article placed successfully:', response.data);

                    // Check if extra slot was used and provide specific feedback
                    const validation = response.data.validation;
                    if (validation && validation.usedExtraSlot) {
                        console.log('✅ Article placed in extra slot:', validation.extraSlot);
                        this.showSnackbar(`✅ Artikel in Sperrgut-Regal eingelagert: ${validation.extraSlot}`, 'success');
                    } else {
                        this.showSnackbar('✅ Artikel erfolgreich eingelagert', 'success');
                    }

                    // Refresh unconfirmed articles list
                    this.fetchUnconfirmedArticles();

                    // Clear found article highlight
                    this.foundArticleHashcode = null;

                    this.state = 'success';
                }
            } catch (error) {
                console.error('❌ Backend error:', error);
                this.handleBackendError(error);
            } finally {
                // Hide loading indicator
                this.isProcessing = false;
                this.loadingMessage = '';
            }
        },

        handleBackendError(error) {
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const errorCode = errorData.code;
                const errorMessage = errorData.error;

                console.log('📋 Error code:', errorCode);
                console.log('📋 Error message:', errorMessage);

                switch (errorCode) {
                    case 'ARTICLE_NOT_FOUND':
                        console.log('❌ Article not found in database');
                        this.showSnackbar(`❌ Artikel nicht gefunden: ${this.scannedArticle.hashcode}`, 'error');
                        this.mockData.wrongArticleLocation = '';
                        this.state = 'error_article';
                        break;

                    case 'ARTICLE_ALREADY_SCANNED':
                        console.log('❌ Article already scanned');
                        this.mockData.wrongSku = this.scannedArticle.sku;
                        this.mockData.wrongArticleLocation = this.scannedArticle.slot;
                        this.showSnackbar('❌ Artikel bereits eingelagert', 'warning');
                        this.state = 'error_article';
                        break;

                    case 'SLOT_DOES_NOT_MATCH':
                        console.log('❌ Slot does not match:', {
                            scanned: this.scannedShelfSlot,
                            expected: this.scannedArticle.slot,
                            details: errorData.details
                        });

                        this.mockData.scannedLocation = this.scannedShelfSlot;

                        // Check the specific validation reason from backend
                        const validationReason = errorData.details?.reason;
                        const allowedExtraSlot = errorData.details?.allowedExtraSlot;

                        if (validationReason === 'WRONG_EXTRA_SLOT' && allowedExtraSlot) {
                            // User scanned wrong extra slot
                            console.log('❌ Wrong extra slot. Expected:', allowedExtraSlot, 'Got:', this.scannedShelfSlot);
                            this.showSnackbar(
                                `❌ Falsches Sperrgut-Regal: ${this.scannedShelfSlot}\n✅ Bitte scannen: ${allowedExtraSlot}`,
                                'error'
                            );
                        } else if (validationReason === 'SLOT_MISMATCH' && allowedExtraSlot) {
                            // Slots don't match, but extra slot is available as alternative
                            console.log('❌ Slot mismatch. Expected:', this.scannedArticle.slot, 'Or extra slot:', allowedExtraSlot);
                            this.showSnackbar(
                                `❌ Falsches Regal: ${this.scannedShelfSlot}\n✅ Erwartet: ${this.scannedArticle.slot} oder ${allowedExtraSlot}`,
                                'error'
                            );
                        } else if (validationReason === 'MISSING_SLOT_INFO') {
                            // Missing slot information (shouldn't happen normally)
                            console.log('❌ Missing slot information');
                            this.showSnackbar('❌ Fehlende Regalinformationen', 'error');
                        } else {
                            // Normal slot mismatch
                            console.log('❌ Normal slot mismatch. Expected:', this.scannedArticle.slot, 'Got:', this.scannedShelfSlot);
                            this.showSnackbar(
                                `❌ Falsches Regal: ${this.scannedShelfSlot}\n✅ Erwartet: ${this.scannedArticle.slot}`,
                                'error'
                            );
                        }

                        this.state = 'error_slot';
                        break;

                    default:
                        console.error('❌ Unknown error code:', errorCode);
                        this.showSnackbar(`❌ Fehler: ${errorMessage || 'Unbekannter Fehler'}`, 'error');
                        break;
                }
            } else {
                this.showSnackbar('❌ Verbindungsfehler zum Server', 'error');
                console.error('Network error:', error.message);
            }
        },

        // Unconfirmed Articles Methods
        scrollToArticle(hashcode) {
            const tableContainer = document.querySelector('.table-scroll-container');
            if (!tableContainer) {
                console.warn('Table container not found');
                return;
            }

            const rows = tableContainer.querySelectorAll('tbody tr');
            let targetRow = null;

            rows.forEach(row => {
                const rowData = row.textContent || '';
                if (rowData.includes(hashcode.toString())) {
                    targetRow = row;
                }
            });

            if (targetRow) {
                targetRow.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });

                console.log('✅ Scrolled to article row');
            } else {
                console.warn('Article row not found in table');
            }
        },

        isArticleFound(hashcode) {
            return this.foundArticleHashcode === hashcode;
        },

        getRowClass(item) {
            const isFound = Number(item.hashcode) === Number(this.foundArticleHashcode);
            if (isFound) {
                console.log('🎯 Highlighting row for hashcode:', item.hashcode);
                return 'found-article-row';
            }
            return '';
        },

        async fetchUnconfirmedArticles() {
            this.loadingUnconfirmed = true;
            try {
                const response = await PacklistService.getUnconfirmedMulticartItems();
                if (response.status === 200 && response.data && response.data.articles) {
                    this.unconfirmedArticles = response.data.articles;
                    console.log('📋 Unconfirmed articles loaded:', this.unconfirmedArticles.length);
                    this.showSnackbar(`✅ ${this.unconfirmedArticles.length} unbestätigte Artikel geladen`, 'success');
                }
            } catch (error) {
                console.error('❌ Error fetching unconfirmed articles:', error);
                this.showSnackbar('❌ Fehler beim Laden der unbestätigten Artikel', 'error');
            } finally {
                this.loadingUnconfirmed = false;
            }
        },

        // Dev Mode Methods
        handleDevSetState(newState) {
            this.state = newState;
            console.log('[DevMode] State changed to:', newState);
        },

        handleDevArticleScan({ scanData, mockData, articleStatus }) {
            console.log('[DevMode] Simulating article scan:', scanData);
            console.log('[DevMode] Article status:', articleStatus);

            // Update dev mode article status
            this.devArticleStatus = articleStatus || 'ARTICLE_NOT_FOUND';

            // Update mock data with the provided data
            this.mockData = {
                ...this.mockData,
                ...mockData,
                shelfLocation: scanData.slot
            };

            // Process the scan
            this.handleArticleScan(scanData);
        },

        handleDevShelfScan({ scanData }) {
            console.log('[DevMode] Simulating shelf scan with real backend:', scanData);

            // Process the scan (will call real backend)
            this.handleShelfScan(scanData);
        }
    },
    mounted() {
        this.updateTime();

        // Update time every second
        this.timeInterval = setInterval(() => {
            this.updateTime();
        }, 1000);

        // Setup window resize listener
        window.addEventListener('resize', this.handleResize);

        // Initialize fullscreen support
        this.checkFullscreenSupport();

        // Add fullscreen event listeners
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);

        // Setup tablet/mobile mode if applicable
        this.$nextTick(() => {
            this.setupTabletViewport();
            if ((this.isTablet && this.$route.query.tablet) || (this.isMobile && this.$route.query.mobile)) {
                console.log('[Device] Entering fullscreen on initial load');
                this.enterFullscreen();
            }
        });

        // Load unconfirmed articles on mount (only in desktop mode)
        if (this.isDesktop) {
            this.fetchUnconfirmedArticles();
        }

        // Setup scanner input field for mobile/tablet
        if (!this.isDesktop) {
            // Check for debug mode
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('debug') === 'true') {
                this.scannerOptions.debug = true;
            }
        }
    },
    beforeDestroy() {
        if (this.timeInterval) {
            clearInterval(this.timeInterval);
        }

        this.stopCountdown();

        window.removeEventListener('resize', this.handleResize);

        // Remove fullscreen event listeners
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);

        // Exit fullscreen if active
        if (this.isFullscreen) {
            this.exitFullscreen();
        }
    }
};
</script>

<style scoped>
/* Main Container */
.placement-wizard {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    background: #ffffff;
    box-sizing: border-box;
    overflow: hidden;
}

.placement-wizard.tablet-mode {
    background: #f8f9fa;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
}

/* Tablet Mode Badge */
.tablet-mode-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1000;
}

.socket-debug-info {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

/* Dev Mode Toggle */
.dev-mode-toggle {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1000;
}

.dev-mode-toggle .v-btn.dev-active {
    animation: pulse-dev 1.5s ease-in-out infinite;
}

@keyframes pulse-dev {
    0%, 100% {
        box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
    }
    50% {
        box-shadow: 0 0 0 10px rgba(255, 152, 0, 0);
    }
}

/* Back Button */
.back-button {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 1000;
}

/* Fullscreen Toggle */
.fullscreen-toggle {
    position: absolute;
    top: 70px;
    left: 16px;
    z-index: 1000;
}

/* Main Container - Flexbox Layout */
.placement-main-container {
    display: flex;
    flex: 1;
    gap: 0;
    overflow: hidden;
    height: 100vh;
    height: 100dvh;
}

.placement-wizard.tablet-mode .placement-main-container {
    height: 100vh;
    height: 100dvh;
}

/* Content Wrapper - Left Side */
.placement-content-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
    position: relative;
}

/* Loading Overlay */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding: 32px;
}

.loading-text {
    font-weight: 600;
    color: #1976d2;
    text-align: center;
    animation: pulse-text 1.5s ease-in-out infinite;
}

@keyframes pulse-text {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.6;
    }
}

/* Mobile Loading Styles */
@media (max-width: 768px) {
    .loading-overlay {
        background: rgba(255, 255, 255, 0.98);
    }

    .loading-content {
        gap: 32px;
        padding: 48px 24px;
    }

    .loading-text {
        font-size: 1.25rem;
        max-width: 80%;
    }
}

@media (max-width: 480px) {
    .loading-content {
        gap: 40px;
        padding: 60px 24px;
    }

    .loading-text {
        font-size: 1.5rem;
        font-weight: 700;
        letter-spacing: 0.5px;
    }
}

/* Unconfirmed Articles Section - Right Side */
.unconfirmed-section {
    flex: 0 0 50%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100vh;
    height: 100dvh;
}

.unconfirmed-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.unconfirmed-header {
    flex-shrink: 0;
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
}

.unconfirmed-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.search-container {
    flex-shrink: 0;
    background: white;
}

.table-scroll-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
}

.unconfirmed-table {
    height: 100%;
}

/* Table Cell Content Alignment */
.table-cell-content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-height: 48px;
}

/* Custom scrollbar for table */
.table-scroll-container::-webkit-scrollbar {
    width: 8px;
}

.table-scroll-container::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.table-scroll-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.table-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* Found Article Row Highlight */
.unconfirmed-table::v-deep .found-article-row {
    background-color: #c8e6c9 !important;
    animation: highlight-pulse 2s ease-in-out;
}

.unconfirmed-table::v-deep .found-article-row:hover {
    background-color: #a5d6a7 !important;
}

.unconfirmed-table::v-deep .found-article-row td {
    background-color: #c8e6c9 !important;
}

.unconfirmed-table::v-deep .found-article-row:hover td {
    background-color: #a5d6a7 !important;
}

@keyframes highlight-pulse {
    0%, 100% {
        background-color: #c8e6c9;
    }
    50% {
        background-color: #81c784;
    }
}

/* Scanner Input Field - Hidden by default, visible in debug mode */
/* Scanner input styles are now in global /src/styles/scanner-input.css */
</style>
